import React, { useState, useEffect, useCallback } from 'react';
import { Book, Search, Bookmark, Settings, Moon, Sun, ChevronLeft, ChevronRight, Share2, Edit3, Home, List, Grid, Download, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  getTranslations,
  getBooks,
  getChapter,
  searchBible,
  addBookmark,
  addNote,
  clearCache,
  Translation,
  Book as BibleBook,
  Chapter
} from '../lib/bible';
import { addBookmark as addUserBookmark, isVerseBookmarked } from '../lib/bible-bookmarks';
import AuthRequiredModal from '../components/AuthRequiredModal';

interface ReadingSettings {
  fontSize: number;
  darkMode: boolean;
  translation: string;
  viewMode: 'list' | 'grid';
}

export default function Bible() {
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [books, setBooks] = useState<BibleBook[]>([]);
  const [currentTranslation, setCurrentTranslation] = useState<string>('');
  const [currentBook, setCurrentBook] = useState<BibleBook | null>(null);
  const [currentChapter, setCurrentChapter] = useState<Chapter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [settings, setSettings] = useState<ReadingSettings>({
    fontSize: 16,
    darkMode: false,
    translation: '',
    viewMode: 'list'
  });
  const [showSettings, setShowSettings] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [bookmarkedVerses, setBookmarkedVerses] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  // Load initial data
  useEffect(() => {
    checkAuthStatus();
    loadTranslations();
    
    // Load saved settings
    const savedSettings = localStorage.getItem('bibleSettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
      if (parsed.translation) {
        setCurrentTranslation(parsed.translation);
      }
    }
  }, []);

  // Load books when translation changes
  useEffect(() => {
    if (currentTranslation) {
      loadBooks(currentTranslation);
    }
  }, [currentTranslation]);

  // Load chapter when book or chapter number changes
  useEffect(() => {
    if (currentBook && currentChapter) {
      loadChapterContent();
    }
  }, [currentBook, currentChapter]);

  const checkAuthStatus = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setIsLoggedIn(!!session);
  };

  const loadTranslations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTranslations();
      setTranslations(data || []);
      
      // Set default translation if none selected
      if (!currentTranslation && data?.length > 0) {
        setCurrentTranslation(data[0].id);
        updateSettings({ translation: data[0].id });
      }
    } catch (err) {
      setError('Não foi possível carregar as traduções. Por favor, tente novamente mais tarde.');
      console.error('Error loading translations:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadBooks = async (translationId: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getBooks(translationId);
      setBooks(data || []);
      
      // Set default book if none selected
      if (!currentBook && data?.length > 0) {
        setCurrentBook(data[0]);
        
        // Set default chapter if available
        if (data[0].chapters?.length > 0) {
          setCurrentChapter(data[0].chapters[0]);
        }
      }
    } catch (err) {
      setError('Não foi possível carregar os livros. Por favor, tente novamente mais tarde.');
      console.error('Error loading books:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadChapterContent = async () => {
    if (!currentBook || !currentChapter) return;

    try {
      setLoading(true);
      setError(null);
      const chapter = await getChapter(currentTranslation, currentChapter.id);
      setCurrentChapter(chapter);

      // Check bookmarks if user is logged in
      if (isLoggedIn && chapter?.verses) {
        const bookmarkedSet = new Set<string>();
        for (const verse of chapter.verses) {
          const isBookmarked = await isVerseBookmarked(currentBook.id, parseInt(chapter.number), parseInt(verse.number));
          if (isBookmarked) {
            bookmarkedSet.add(`${currentBook.id}-${chapter.number}-${verse.number}`);
          }
        }
        setBookmarkedVerses(bookmarkedSet);
      }
    } catch (err) {
      setError('Não foi possível carregar o texto do capítulo. Por favor, tente novamente mais tarde.');
      console.error('Error loading chapter:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !currentTranslation) return;
    
    try {
      setLoading(true);
      setError(null);
      const results = await searchBible(currentTranslation, searchQuery);
      setSearchResults(results || []);
      setShowSearch(true);
    } catch (err) {
      setError('Não foi possível realizar a busca. Por favor, tente novamente mais tarde.');
      console.error('Error searching:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBookmark = async (verseNumber: string) => {
    if (!currentBook || !currentChapter) return;

    try {
      if (!isLoggedIn) {
        setIsAuthModalOpen(true);
        return;
      }
      
      const verseKey = `${currentBook.id}-${currentChapter.number}-${verseNumber}`;
      const isCurrentlyBookmarked = bookmarkedVerses.has(verseKey);
      
      if (isCurrentlyBookmarked) {
        setBookmarkedVerses(prev => {
          const newSet = new Set(prev);
          newSet.delete(verseKey);
          return newSet;
        });
      } else {
        const bookmark = {
          book_id: currentBook.id,
          chapter: parseInt(currentChapter.number),
          verse: parseInt(verseNumber),
          user_id: ''
        };
        
        await addUserBookmark(bookmark);
        setBookmarkedVerses(prev => {
          const newSet = new Set(prev);
          newSet.add(verseKey);
          return newSet;
        });
      }
      
      alert(isCurrentlyBookmarked ? 'Marcador removido com sucesso!' : 'Marcador adicionado com sucesso!');
    } catch (err) {
      console.error('Error managing bookmark:', err);
      alert('Erro ao gerenciar marcador');
    }
  };

  const handleAddNote = async (verseNumber: string, note: string) => {
    if (!currentBook || !currentChapter) return;

    try {
      if (!isLoggedIn) {
        setIsAuthModalOpen(true);
        return;
      }
      
      const noteData = {
        book_id: currentBook.id,
        chapter: parseInt(currentChapter.number),
        verse: parseInt(verseNumber),
        text: note,
        user_id: ''
      };
      
      await addNote(noteData);
      alert('Nota adicionada com sucesso!');
    } catch (err) {
      console.error('Error adding note:', err);
      alert('Erro ao adicionar nota');
    }
  };

  const handleShare = async (verse: { number: string; text: string }) => {
    if (!currentBook || !currentChapter) return;

    const text = `${currentBook.name} ${currentChapter.number}:${verse.number} - ${verse.text}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Compartilhar versículo',
          text: text
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      await navigator.clipboard.writeText(text);
      alert('Versículo copiado para a área de transferência!');
    }
  };

  const updateSettings = (newSettings: Partial<ReadingSettings>) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);
    localStorage.setItem('bibleSettings', JSON.stringify(updated));
  };

  const handleRefresh = async () => {
    clearCache();
    await loadTranslations();
  };

  const downloadChapter = () => {
    if (!currentBook || !currentChapter?.verses) return;
    
    let content = `# ${currentBook.name} ${currentChapter.number}\n\n`;
    
    currentChapter.verses.forEach(verse => {
      content += `**${verse.number}** ${verse.text}\n\n`;
    });
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentBook.name}_${currentChapter.number}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderVerse = useCallback(({ verse }: { verse: { number: string; text: string } }) => {
    if (!currentBook || !currentChapter) return null;

    const verseKey = `${currentBook.id}-${currentChapter.number}-${verse.number}`;
    const isBookmarked = bookmarkedVerses.has(verseKey);
    
    return (
      <div
        key={verse.number}
        className={`p-4 flex items-start gap-4 ${
          settings.darkMode ? 'text-gray-100' : 'text-gray-800'
        } ${parseInt(verse.number) % 2 === 0 ? (settings.darkMode ? 'bg-gray-800/30' : 'bg-gray-50/50') : ''}`}
      >
        <span className="text-sm text-brand-teal font-medium">{verse.number}</span>
        <div className="flex-1">
          <p style={{ fontSize: `${settings.fontSize}px` }}>{verse.text}</p>
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => handleBookmark(verse.number)}
              className={`p-1 ${isBookmarked ? 'text-brand-teal' : 'hover:bg-gray-100 text-gray-400'} rounded-full`}
              title={isBookmarked ? "Remover marcador" : "Adicionar marcador"}
            >
              <Bookmark className="h-4 w-4" fill={isBookmarked ? "currentColor" : "none"} />
            </button>
            <button
              onClick={() => handleShare(verse)}
              className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600"
              title="Compartilhar"
            >
              <Share2 className="h-4 w-4" />
            </button>
            <button
              onClick={() => {
                const note = prompt('Adicionar anotação:');
                if (note) handleAddNote(verse.number, note);
              }}
              className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600"
              title="Adicionar anotação"
            >
              <Edit3 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }, [currentBook, currentChapter, settings, bookmarkedVerses]);

  const renderVerseGrid = useCallback(({ verse }: { verse: { number: string; text: string } }) => {
    if (!currentBook || !currentChapter) return null;

    const verseKey = `${currentBook.id}-${currentChapter.number}-${verse.number}`;
    const isBookmarked = bookmarkedVerses.has(verseKey);
    
    return (
      <div
        key={verse.number}
        className={`p-4 rounded-lg ${
          settings.darkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-800 shadow-sm'
        }`}
      >
        <div className="flex justify-between items-start mb-2">
          <span className="text-sm bg-brand-teal text-white px-2 py-1 rounded-full">{verse.number}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleBookmark(verse.number)}
              className={`p-1 ${isBookmarked ? 'text-brand-teal' : 'text-gray-400 hover:text-gray-600'} rounded-full`}
              title={isBookmarked ? "Remover marcador" : "Adicionar marcador"}
            >
              <Bookmark className="h-4 w-4" fill={isBookmarked ? "currentColor" : "none"} />
            </button>
            <button
              onClick={() => handleShare(verse)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-full"
              title="Compartilhar"
            >
              <Share2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        <p style={{ fontSize: `${settings.fontSize}px` }} className="mb-2">{verse.text}</p>
        <button
          onClick={() => {
            const note = prompt('Adicionar anotação:');
            if (note) handleAddNote(verse.number, note);
          }}
          className="text-xs text-brand-teal hover:underline flex items-center gap-1"
        >
          <Edit3 className="h-3 w-3" />
          Adicionar nota
        </button>
      </div>
    );
  }, [currentBook, currentChapter, settings, bookmarkedVerses]);

  return (
    <div className={`min-h-screen ${settings.darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${settings.darkMode ? 'bg-gray-800' : 'bg-white'} border-b border-gray-200 p-4`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Book className={`h-8 w-8 ${settings.darkMode ? 'text-brand-light' : 'text-brand-teal'}`} />
            <h1 className={`text-2xl font-bold ${settings.darkMode ? 'text-white' : 'text-brand-teal'}`}>Bíblia</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleRefresh}
              className={`p-2 rounded-full ${settings.darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              title="Atualizar"
            >
              <RefreshCw className={`h-5 w-5 ${settings.darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-full ${settings.darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              title="Configurações"
            >
              <Settings className={`h-5 w-5 ${settings.darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
            <button
              onClick={() => updateSettings({ darkMode: !settings.darkMode })}
              className={`p-2 rounded-full ${settings.darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              title={settings.darkMode ? 'Modo claro' : 'Modo escuro'}
            >
              {settings.darkMode ? (
                <Sun className="h-5 w-5 text-gray-300" />
              ) : (
                <Moon className="h-5 w-5 text-gray-600" />
              )}
            </button>
            <button
              onClick={() => updateSettings({ viewMode: settings.viewMode === 'list' ? 'grid' : 'list' })}
              className={`p-2 rounded-full ${settings.darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              title={`Modo ${settings.viewMode === 'list' ? 'grade' : 'lista'}`}
            >
              {settings.viewMode === 'list' ? (
                <Grid className={`h-5 w-5 ${settings.darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
              ) : (
                <List className={`h-5 w-5 ${settings.darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
              )}
            </button>
            <button
              onClick={downloadChapter}
              className={`p-2 rounded-full ${settings.darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              title="Baixar capítulo"
              disabled={!currentChapter}
            >
              <Download className={`h-5 w-5 ${settings.darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
            <button
              onClick={() => navigate('/')}
              className={`flex items-center gap-2 px-4 py-2 ${
                settings.darkMode 
                  ? 'bg-brand-light text-brand-teal' 
                  : 'bg-brand-teal text-white'
              } rounded-lg hover:opacity-90 transition-colors`}
            >
              <Home className="h-4 w-4" />
              <span>Início</span>
            </button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className={`mb-4 p-4  rounded-lg ${settings.darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <h3 className={`font-medium mb-2 ${settings.darkMode ? 'text-white' : 'text-gray-900'}`}>Configurações de Leitura</h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className={`block text-sm mb-1 ${settings.darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Tamanho da Fonte
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateSettings({ fontSize: Math.max(12, settings.fontSize - 2) })}
                    className={`p-1 rounded ${settings.darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-700'}`}
                  >
                    -
                  </button>
                  <span className={settings.darkMode ? 'text-white' : 'text-gray-900'}>
                    {settings.fontSize}px
                  </span>
                  <button
                    onClick={() => updateSettings({ fontSize: Math.min(24, settings.fontSize + 2) })}
                    className={`p-1 rounded ${settings.darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-700'}`}
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="flex-1">
                <label className={`block text-sm mb-1 ${settings.darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Tradução
                </label>
                <select
                  value={currentTranslation}
                  onChange={(e) => {
                    setCurrentTranslation(e.target.value);
                    updateSettings({ translation: e.target.value });
                  }}
                  className={`w-full px-3 py-2 rounded-lg ${
                    settings.darkMode
                      ? 'bg-gray-600 text-white border-gray-500'
                      : 'bg-white text-gray-900 border-gray-300'
                  }`}
                >
                  {translations?.map(translation => (
                    <option key={translation.id} value={translation.id}>
                      {translation.name} ({translation.language.name})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${settings.darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            <input
              type="text"
              placeholder="Buscar na Bíblia..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              className={`pl-10 w-full px-4 py-2 border ${
                settings.darkMode 
                  ? 'bg-gray-700 text-white border-gray-600' 
                  : 'bg-white text-gray-900 border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal`}
            />
            <button
              onClick={handleSearch}
              className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 ${
                settings.darkMode
                  ? 'bg-gray-600 text-white hover:bg-gray-500'
                  : 'bg-brand-teal text-white hover:bg-brand-teal/90'
              } rounded transition-colors`}
            >
              Buscar
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <select
            value={currentBook?.id || ''}
            onChange={(e) => {
              const book = books.find(b => b.id === e.target.value);
              if (book) {
                setCurrentBook(book);
                if (book.chapters?.length > 0) {
                  setCurrentChapter(book.chapters[0]);
                }
              }
            }}
            className={`w-full sm:w-auto px-4 py-2 rounded-lg ${
              settings.darkMode
                ? 'bg-gray-700 text-white border-gray-600'
                : 'bg-white text-gray-900 border-gray-300'
            }`}
          >
            <option value="">Selecione um livro</option>
            {books?.map(book => (
              <option key={book.id} value={book.id}>{book.name}</option>
            ))}
          </select>

          {currentBook && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const currentIndex = currentBook.chapters?.findIndex(ch => ch.id === currentChapter?.id) ?? -1;
                  if (currentIndex > 0) {
                    setCurrentChapter(currentBook.chapters[currentIndex - 1]);
                  }
                }}
                disabled={!currentChapter || (currentBook.chapters?.[0]?.id === currentChapter?.id)}
                className={`p-2 rounded-full ${
                  settings.darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                } disabled:opacity-50`}
              >
                <ChevronLeft className={`h-5 w-5 ${settings.darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
              </button>
              <select
                value={currentChapter?.id || ''}
                onChange={(e) => {
                  const chapter = currentBook.chapters?.find(ch => ch.id === e.target.value);
                  if (chapter) {
                    setCurrentChapter(chapter);
                  }
                }}
                className={`px-4 py-2 rounded-lg ${
                  settings.darkMode
                    ? 'bg-gray-700 text-white border-gray-600'
                    : 'bg-white text-gray-900 border-gray-300'
                }`}
              >
                <option value="">Selecione um capítulo</option>
                {currentBook.chapters?.map(chapter => (
                  <option key={chapter.id} value={chapter.id}>
                    Capítulo {chapter.number}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  const currentIndex = currentBook.chapters?.findIndex(ch => ch.id === currentChapter?.id) ?? -1;
                  if (currentIndex < (currentBook.chapters?.length ?? 0) - 1) {
                    setCurrentChapter(currentBook.chapters[currentIndex + 1]);
                  }
                }}
                disabled={!currentChapter || (currentBook.chapters?.[currentBook.chapters.length - 1]?.id === currentChapter?.id)}
                className={`p-2 rounded-full ${
                  settings.darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                } disabled:opacity-50`}
              >
                <ChevronRight className={`h-5 w-5 ${settings.darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
          </div>
        ) : error ? (
          <div className={`p-4 rounded-lg ${settings.darkMode ? 'bg-red-900/20 text-red-200' : 'bg-red-50 text-red-800'}`}>
            <p>{error}</p>
          </div>
        ) : showSearch && searchResults?.length > 0 ? (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-semibold ${settings.darkMode ? 'text-white' : 'text-gray-900'}`}>
                Resultados da busca: {searchQuery}
              </h2>
              <button
                onClick={() => setShowSearch(false)}
                className={`px-3 py-1 ${
                  settings.darkMode
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } rounded-lg transition-colors`}
              >
                Voltar
              </button>
            </div>
            <div className="space-y-4">
              {searchResults.map((result, index) => (
                <div
                  key={`search-${index}`}
                  className={`p-4 rounded-lg ${
                    settings.darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800 shadow-sm'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-brand-teal">
                      {result.reference}
                    </span>
                    <button
                      onClick={() => {
                        const book = books?.find(b => b.id === result.bookId);
                        if (book) {
                          setCurrentBook(book);
                          const chapter = book.chapters?.find(ch => ch.number === result.chapterNumber);
                          if (chapter) {
                            setCurrentChapter(chapter);
                            setShowSearch(false);
                          }
                        }
                      }}
                      className={`text-xs px-2 py-1 ${
                        settings.darkMode
                          ? 'bg-gray-700 text-white hover:bg-gray-600'
                          : 'bg-brand-teal text-white hover:bg-brand-teal/90'
                      } rounded transition-colors`}
                    >
                      Ir para
                    </button>
                  </div>
                  <p style={{ fontSize: `${settings.fontSize}px` }}>{result.text}</p>
                </div>
              ))}
            </div>
          </div>
        ) : currentChapter?.verses ? (
          <div className={settings.viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-1 bg-white rounded-lg shadow-sm'}>
            {currentChapter.verses.map((verse) => (
              settings.viewMode === 'grid' 
                ? renderVerseGrid({ verse })
                : renderVerse({ verse })
            ))}
          </div>
        ) : (
          <div className={`p-4 rounded-lg text-center ${settings.darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
            <p>Selecione um livro e um capítulo para começar a leitura.</p>
          </div>
        )}
      </div>

      {/* Auth Modal */}
      <AuthRequiredModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        message="Para salvar marcadores e anotações, você precisa estar logado."
        returnTo="/bible"
      />
    </div>
  );
}